"use client";

/**
 * Skeletons used while the markets feed is loading.
 *
 * `MarketCardSkeleton` mirrors the dimensions of a real EventCard
 * (`bg-white … h-[230px] flex flex-col`, header p-3.5 pb-3 + flex-1
 * trading block + thin footer row) so replacing the skeleton with
 * content doesn't jump the layout when data arrives. The 230px height
 * is tuned to the real average — measured across binary and multi-
 * outcome cards.
 *
 * `LoadingSkeleton` wraps a grid of skeleton cards and, on the crypto
 * dashboard, prepends ticker + dashboard placeholders so the header
 * area doesn't pop in late.
 */
/**
 * Simple skeleton card used inline by For-You / Bookmarks / Breaking
 * loading states. Lighter than `MarketCardSkeleton` (no image slot, no
 * footer row, no fixed height) — just a title + subtitle + two buttons.
 *
 *   <SimpleCardSkeleton count={8} />
 *
 * `count` defaults to 8; pass 4 for compact tabs.
 */
export function SimpleCardSkeleton() {
  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 animate-pulse">
      <div className="h-4 bg-gray-200 dark:bg-[#25252B] rounded w-3/4 mb-3" />
      <div className="h-3 bg-gray-200 dark:bg-[#25252B] rounded w-1/2 mb-4" />
      <div className="flex gap-2">
        <div className="h-8 bg-gray-200 dark:bg-[#25252B] rounded flex-1" />
        <div className="h-8 bg-gray-200 dark:bg-[#25252B] rounded flex-1" />
      </div>
    </div>
  );
}

export function MarketCardSkeleton() {
  return (
    <div className="bg-white dark:bg-white/[0.04] rounded-[5px] border border-gray-200/80 dark:border-[#1A1A1A] h-[230px] flex flex-col animate-pulse overflow-hidden">
      <div className="p-3.5 pb-3 flex gap-3 items-center">
        <div className="w-12 h-12 bg-gray-200 dark:bg-white/[0.06] rounded-[5px] flex-shrink-0" />
        <div className="flex-1 space-y-2 min-w-0">
          <div className="h-3.5 bg-gray-200 dark:bg-white/[0.06] rounded w-full" />
          <div className="h-3.5 bg-gray-200 dark:bg-white/[0.06] rounded w-2/3" />
        </div>
      </div>
      <div className="px-4 pb-3 flex-1 flex flex-col justify-center gap-2">
        <div className="flex items-center gap-2">
          <div className="h-3 bg-gray-200 dark:bg-white/[0.06] rounded flex-1" />
          <div className="h-8 w-16 bg-gray-200 dark:bg-white/[0.06] rounded-[5px]" />
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 bg-gray-200 dark:bg-white/[0.06] rounded flex-1" />
          <div className="h-8 w-16 bg-gray-200 dark:bg-white/[0.06] rounded-[5px]" />
        </div>
      </div>
      <div className="px-3.5 pb-2 pt-1.5 flex items-center justify-between">
        <div className="h-2.5 w-16 bg-gray-200 dark:bg-white/[0.06] rounded" />
        <div className="h-2.5 w-12 bg-gray-200 dark:bg-white/[0.06] rounded" />
      </div>
    </div>
  );
}

export function LoadingSkeleton({ category, isCollapsed }: { category: string; isCollapsed: boolean }) {
  return (
    <div className="animate-pulse">
      {category === "crypto" && (
        <div className="mb-6 space-y-4">
          {/* Price ticker row */}
          <div className="flex gap-3 overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-36 h-16 bg-white dark:bg-white/[0.04] rounded-[5px] border border-gray-200/80 dark:border-[#1A1A1A] p-3 space-y-2"
              >
                <div className="h-3 bg-gray-200 dark:bg-white/[0.06] rounded w-12" />
                <div className="h-4 bg-gray-200 dark:bg-white/[0.06] rounded w-20" />
              </div>
            ))}
          </div>
          {/* Dashboard cards row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="h-44 bg-white dark:bg-white/[0.04] rounded-[5px] border border-gray-200/80 dark:border-[#1A1A1A]" />
            <div className="h-44 bg-white dark:bg-white/[0.04] rounded-[5px] border border-gray-200/80 dark:border-[#1A1A1A]" />
            <div className="h-44 bg-white dark:bg-white/[0.04] rounded-[5px] border border-gray-200/80 dark:border-[#1A1A1A]" />
          </div>
        </div>
      )}
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-8 ${
          isCollapsed ? "2xl:grid-cols-4 3xl:grid-cols-5" : "2xl:grid-cols-4"
        }`}
      >
        {[...Array(8)].map((_, i) => (
          <MarketCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}
